import { nanoid } from "@reduxjs/toolkit";
import { api } from "../../app/api";
import { FilterType } from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { EventPayload } from "../../common/types/Event";
import { Nullable } from "../../common/types/nullable";
import { Options } from "../../common/types/requestOptions";
import { CustomerOrganizationWithCostCenters } from "../customer/customerAPI";
import { Address } from "../customer/types/queries";

export interface TimeRange {
  start: number;
  end: number;
}

export interface AccountingDataRow {
  type: string;
  id: string;
  version?: number;
  date?: string;
  expense?: string;
  amount?: string;
  unit?: string;
  eezyCompanyId?: string;
  eezyCostCenterId?: string;
  employmentId?: string;
  talentId?: string;
  tesId?: string;
  salaryPeriodClassId?: string;
  customerOrganizationId?: string;
  customerCostCenterId?: string;
  assignmentId?: string;
  workTitleId?: string;
  workLocationId?: string;
  contractId?: string;
  salesPersonId?: string;
  entryId?: string;
  expenseTypeCode?: string;
  isBillable?: boolean;
  isMonthly?: boolean;
  isShift?: boolean;
  isExpressOrder?: boolean;
  isSelfServiceOrder?: boolean;
  isEarlyBirdOrder?: boolean;
  isCustomerRecruit?: boolean;
  isPayable?: boolean;
  timeRange?: TimeRange;
  unitCost?: string;
  unitPriceOverride?: string;
  expenseRowId?: string;
  payslipId?: string;
  invoiceId?: string;
  invoiceUnitPrice?: string;
  invoiceAmount?: string;
  vatRate?: string;
  vatAmount?: string;
  accountDebit?: string;
  accountCredit?: string;
  accountSales?: string;
  invoicePriceMultiplier?: string;
  voided?: boolean;
}

export interface ExamineInvoiceResponse {
  data: AccountingDataRow[];
}

export interface Seller {
  businessId: string;
  name: string;
  taxCode: string;
  address: {
    street: string;
    city: string;
    zip: string;
    country: string;
  };
  webAddress: string;
  emailAddress: string;
  bank: {
    iban: string;
    bic: string;
  };
}

export interface Buyer {
  businessId: string;
  name: string;
  taxCode: string;
  address: {
    street?: string;
    city: string;
    zip: string;
    country: string;
  };
  sellersBuyerIdentifier?: string;
}

export interface InvoiceAttachment {
  filename: string;
  size: number;
  status?: string;
}

// Less detailed version for invoices in general list view
export interface InvoiceDisplay {
  id: string;
  invoiceNumber: string;
  invoiceType: string;
  bundleId?: string;
  originalInvoiceNumber?: string;
  originCode: string;
  originCodeTranslation?: string;
  eezyCompanyId: string;
  eezyCompanyName: string;
  buyerId: string;
  buyerName: string;
  buyerCostCenterId: string;
  buyerCostCenterName: string;
  customerCompanyId?: string;
  invoiceDate?: string;
  invoiceDueDate: string;
  invoiceValueDate: string;
  salesPersonName?: string;
  invoiceStatus: string;
  invoiceStatusTranslation?: string;
  invoiceWave: string;
  vatExcludedAmount?: string;
  vatAmount?: string;
  vatIncludedAmount?: string;
  version?: number;
}

export type InvoiceBreakDownRows = Nullable<string[]>;

interface InvoiceFactoringProvider {
  address: Address;
  iban: string;
  factoringContractId: string;
  name: string;
  businessId: string;
  bic: string;
  transferClause: string[];
}

export enum InvoiceLanguage {
  FI = "FI",
  SV = "SV",
  EN = "EN",
}

// More detailed version for single invoice viewing and editing
export interface InvoiceEdit {
  id: string;
  invoiceNumber?: string;
  originalInvoiceNumber: string | null;
  bundleId: string | null;
  invoiceType: string;
  originCode?: string;
  eezyCompanyId: string;
  eezyCompanyName: string;
  eezyBuyerCompanyId?: string;
  eezyBuyerCompanyName?: string;
  eezyCostCenterId?: string;
  eezyCostCenterName?: string;
  customerOrganizationId?: string;
  customerOrganizationName?: string;
  customerCompanyId?: string;
  invoiceDate: string;
  invoiceDueDate: string;
  invoiceValueDate: string;
  salesPersonId?: string;
  salesPersonName?: string;
  buyerContactPersonId?: string;
  buyerContactPersonName?: string;
  invoiceStatus: string;
  invoiceWave?: string;
  vatExcludedAmount?: string;
  vatAmount?: string;
  vatIncludedAmount?: string;
  version?: number;
  sellersBuyerIdentifier: string | null;
  invoiceRows: InvoiceRow[];
  contractId?: string;
  contractName?: string;
  paymentTermDays: string;
  sellerReferenceIdentifier?: string;
  buyerReferenceIdentifier?: string;
  orderIdentifier?: string;
  agreementIdentifier?: string;
  referenceNumber?: string;
  seller: Seller | undefined;
  buyer: Buyer | undefined;
  attachments?: InvoiceAttachment[];
  freeText: string | null;
  breakDownRows: InvoiceBreakDownRows;
  factoringProvider?: InvoiceFactoringProvider;
  deliveryDate: string | null;
  language: InvoiceLanguage;
}

export interface InvoiceRow {
  id: string;
  eezyCostCenterId: string;
  salesAccountCode: string;
  quantityAmount: string;
  quantityUnit: string;
  unitPriceAmount: string;
  customerOrganizationId?: string;
  customerCostCenterId?: string;
  eezyBuyerCostCenterId?: string;
  discountPercent?: string;
  vatRatePercent: string;
  vatCode: string;
  vatExcludedAmount: string;
  vatAmount: string;
  amount: string;
  articleName?: string;
  articleCategory?: string;
  freeText?: string;
  editable: boolean;
  version?: number;
}

export enum ItemGroupLoadingState {
  IDLE = "idle",
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export interface ItemGroup<T> {
  items: Array<T[] | T>;
  name: string;
  year?: string;
  secondaryTextKey?: string;
  subgroupTitleKeys?: Record<number, string>;
  status?: ItemGroupLoadingState;
}

export interface InvoicesFetch {
  data: InvoiceDisplay[];
}

export interface InvoicesEditFetch {
  data: InvoiceEdit[];
}

export interface ManualInvoicePost {
  invoiceType: string;
  invoiceDate: string;
  eezyCompanyId: string;
  customerOrganizationId?: string;
  eezyBuyerCompanyId?: string;
  contractId?: string;
  invoiceValueDate: string;
  paymentTermDays: string;
  sellerReferenceIdentifier?: string;
  buyerReferenceIdentifier?: string;
  orderIdentifier?: string;
  agreementIdentifier?: string;
  originalInvoiceNumber?: string;
  deliveryDate: string | null;
  invoiceRows: InvoiceRow[];
  salesPersonId?: string;
  buyerContactPersonId?: string;
}

export interface InvoiceSavePost {
  invoiceId: string;
  invoiceDate: string;
  invoiceValueDate: string;
  paymentTermDays: string;
  sellerReferenceIdentifier: string;
  buyerReferenceIdentifier: string;
  orderIdentifier: string;
  agreementIdentifier: string;
  salesPersonId?: string;
  buyerContactPersonId?: string;
  buyerContactPersonName?: string;
  deliveryDate: string | null;
  version: number;
  invoiceRows: InvoiceRow[];
}

export interface InvoiceEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export interface InvoiceWavePost {
  invoiceId: string;
  invoiceSentWave: string;
  version: number;
}

export interface PaymentTerm {
  days: number;
  interestRate: string;
  description: string;
}

export interface PaymentTerms {
  version: string;
  paymentTerms: PaymentTerm[];
}

export interface InvoiceFilters {
  search: string;
  filters: {
    invoiceNumbers?: string;
    invoiceTypes?: string;
    originCodes?: string;
    eezyCompanyNames?: string[];
    eezyCostCenterNames?: string;
    customerOrganizationNames?: string;
    invoiceDateFrom?: string;
    invoiceDateTo?: string;
    invoiceDueDateFrom?: string;
    invoiceDueDateTo?: string;
    invoiceValueDateFrom?: string;
    invoiceValueDateTo?: string;
    salesPersonIds?: string;
    invoiceStatuses?: string[];
    invoiceWaves?: string[];
    billingTypes?: string[];
  };
}

export interface SearchFilter {
  name: string;
  value: any;
  type?: FilterType;
}

export interface EezyCompanyFilters { }

export interface EezyCostCenter {
  eezyCostCenterId: string;
  name: string;
  code: string;
}

export interface EezyCompany {
  eezyCompanyId: string;
  name: string;
  businessId: string;
  isFranchise: boolean;
  costCenters: EezyCostCenter[];
}

export interface AttachmentUploadRequest {
  invoiceId: string;
  filename: string;
  size: string;
  url: string;
  content: Blob;
}

export type AttachmentUploadUrlRequest = Omit<
  AttachmentUploadRequest,
  "url" | "content"
>;

export type AttachmentRequest = Omit<AttachmentUploadUrlRequest, "size">;

export interface AttachmentUrl {
  url: string;
}

export interface CreditInvoiceRequest {
  originalInvoiceId: string;
  reason: string;
  invoiceValueDate: string;
  paymentTermDays: string;
}

export type CustomerOrgFilter = {
  id: string;
};

export type LocalStorageAttachment = {
  id: string;
  uploadStarted: number;
  path: string;
  pathId: string;
  filename: string;
  size: number;
  status: string;
};

export interface ConvertToCreditInvoice {
  invoiceId: string;
  originalInvoiceId: string;
}

interface FactoringProvider {
  validFrom: string;
  value: {
    type: string;
    name: string;
    businessId: string;
    iban: string;
    bic: string;
    factoringContractId: number;
    sellerNumber: number;
    address: Address;
  };
}

interface FactoringProviders {
  factoringProviders: FactoringProvider[];
}

export const TYPE_INTERNAL = "Internal";
export const TYPE_EXTERNAL = "External";

export const getInvoices = (
  filters: InvoiceFilters,
  { requestId }: Options = {},
) => {
  const filterObject = filters.filters;
  let params: Record<string, string> = {};
  if (filters.search.toString()) params["search"] = filters.search.toString();
  for (const [key, value] of Object.entries(filterObject)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  return api.get<InvoicesFetch>("/projections/qry/invoices", {
    requestId,
    auth: true,
    queryParams: {
      ...params,
    },
  });
};

export const getEezyCompanies = (
  EezyCompanyFilters: EezyCompanyFilters,
  { requestId }: Options = {},
) => {
  return api
    .get<EezyCompany[]>("/projections/qry/eezyCompanies", {
      requestId,
      auth: true,
    })
    .then((res) => {
      return res.data;
    });
};

export const saveLocalDraft = (
  invoice: InvoiceEdit,
  { requestId }: Options,
) => {
  const promise = new Promise<InvoiceEdit>((resolve, reject) => {
    setTimeout(() => {
      resolve(invoice);
    }, 300);
  });
  return promise;
};

export const getPaymentTerms = (
  filters: EezyCompanyFilters,
  { requestId }: Options = {},
) => {
  return api
    .get<PaymentTerms>("/projections/qry/paymentTerms", {
      requestId,
      auth: true,
    })
    .then((res) => {
      return res.data;
    });
};

export const createNewManualInvoice = (
  invoice: ManualInvoicePost,
  { requestId }: Options,
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/createManualInvoice", {
    requestId,
    auth: true,
    body: {
      ...invoice,
    },
  });
};

export const saveInvoice = (
  invoice: InvoiceSavePost,
  { requestId }: Options,
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/saveInvoice", {
    requestId,
    auth: true,
    body: {
      ...invoice,
    },
  });
};

export const sendInvoicesWithIds = (
  invoiceIds: string[],
  { requestId }: Options,
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/sendInvoices2", {
    requestId,
    auth: true,
    body: {
      invoiceIds,
    },
  });
};

export const updateInvoiceWave = (
  invoiceWave: InvoiceWavePost,
  { requestId }: Options,
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/changeInvoiceWave", {
    requestId,
    auth: true,
    body: {
      ...invoiceWave,
    },
  });
};

export const getInvoiceById = (id: string, { requestId }: Options = {}) => {
  return api.get<InvoiceEdit>("/projections/qry/invoiceById", {
    requestId,
    auth: true,
    queryParams: {
      id,
    },
  });
};

export const getInvoicesByNumbers = (
  numbers: string[],
  { requestId }: Options = {},
) => {
  return api.get<InvoicesEditFetch>("/projections/qry/invoiceDetails", {
    requestId,
    auth: true,
    queryParams: {
      numbers: numbers.toString(),
    },
  });
};

export const deleteInvoiceCmd = (invoiceId: string, { requestId }: Options) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/deleteInvoice", {
    requestId,
    auth: true,
    body: {
      invoiceId,
    },
  });
};

export const deleteInvoicesBundleCmd = (
  bundleId: string,
  { requestId }: Options,
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/deleteInvoicesBundle", {
    requestId,
    auth: true,
    body: {
      bundleId,
    },
  });
};

export const getAttachmentUploadUrl = (
  attachment: AttachmentUploadUrlRequest,
  { requestId }: Options = {},
) => {
  const { invoiceId, filename, size } = attachment;
  return api.get<AttachmentUrl>("/invoicing/qry/invoiceAttachmentUploadUrl", {
    requestId,
    auth: true,
    queryParams: {
      invoiceId,
      filename,
      size,
    },
  });
};

export const getAttachmentDownloadUrl = (
  attachment: AttachmentRequest,
  { requestId }: Options = {},
) => {
  const { invoiceId, filename } = attachment;
  return api.get<AttachmentUrl>("/invoicing/qry/invoiceAttachmentDownloadUrl", {
    requestId,
    auth: true,
    queryParams: {
      invoiceId,
      filename,
    },
  });
};

export const uploadAttachment = (url: string, content: Blob) => {
  return api.upload(url, {
    headers: {},
    body: content,
  });
};

export const examineInvoice = (invoiceId: string, rowId?: string) => {
  const queryParams: Record<string, string> = { invoiceId };
  if (rowId) {
    queryParams.invoiceRowId = rowId;
  }
  return api.get<ExamineInvoiceResponse>("/invoicing/qry/examineInvoice", {
    requestId: nanoid(),
    auth: true,
    queryParams,
  });
};

export const updateBillableRowState = (
  ids: string[],
  isBillable: boolean,
  { requestId }: Options = {},
) => {
  return api.post("/invoicing/cmd/updateBillableRowState", {
    requestId: requestId ?? nanoid(),
    auth: true,
    body: {
      ids,
      isBillable,
    },
  });
};

export const removeAttachment = (
  attachment: AttachmentRequest,
  { requestId }: Options = {},
) => {
  const { invoiceId, filename } = attachment;
  return api.post<InvoiceEvent[]>("/invoicing/cmd/removeAttachment", {
    requestId,
    auth: true,
    body: {
      invoiceId,
      filename,
    },
  });
};

export const createNewCreditInvoice = (
  invoiceData: CreditInvoiceRequest,
  { requestId }: Options = {},
) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/createCreditInvoice", {
    requestId,
    auth: true,
    queryParams: {
      originalInvoiceId: invoiceData.originalInvoiceId,
    },
    body: {
      ...invoiceData,
    },
  });
};

export const customerOrgById = (
  filters: CustomerOrgFilter,
  { requestId }: Options = {},
) => {
  return api.get<CustomerOrganizationWithCostCenters>(
    "/projections/qry/customerOrgById",
    {
      requestId,
      auth: true,
      queryParams: {
        customerOrgId: filters.id,
      },
    },
  );
};

export const refreshInvoice = (invoiceId: string) => {
  return api.post<InvoiceEvent[]>("/invoicing/cmd/refreshInvoice", {
    requestId: nanoid(),
    auth: true,
    body: {
      invoiceId,
    },
  });
};

export const convertToCreditInvoiceCommand = (body: ConvertToCreditInvoice) =>
  api.post<EventPayload<InvoiceEdit>[]>(
    "/invoicing/cmd/convertToCreditInvoice",
    {
      requestId: nanoid(),
      auth: true,
      body,
    },
  );

export const getFactoringProvidersForEezyCompany = (eezyCompanyId: string) =>
  api.get<FactoringProviders>("/projections/qry/factoringProviders", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      eezyCompanyId,
    },
  });
